import React, { ReactElement } from 'react';
import styled from '@emotion/styled';
import { FONT_FAMILIES } from '../../theme/material/constants';
import Button from './Button';
import { DefaultIconV2, UnsuccessfulIcon, VerifiedIcon, WhiteIcon } from '../icons/IDVIcons';
import { isDarkMode } from '../../utils/helpers';

const getButtonStyles = (status: string) => {
  const darkMode = isDarkMode();

  switch (status) {
    case 'getIdVerified':
      return {
        background: darkMode ? '#2B8FD7 !important' : '#F6F4F4 !important',
        color: darkMode ? '#212121' : '#595959 !important',
        border: 'none !important',
        fontWeight: 700,
        fontStyle: 'normal',
        lineHeight: 'normal',
        boxShadow: darkMode ? 'none !important' : '0px 1px 2px 0px rgba(0, 0, 0, 0.20) !important',
        '&:hover, &:active, &:focus': {
          background: darkMode ? '#2B8FD7 !important' : '#F6F4F4 !important',
          color: darkMode ? '#212121' : '#595959 !important',
          border: 'none !important',
          boxShadow: darkMode ? 'none !important' : '0px 1px 2px 0px rgba(0, 0, 0, 0.20) !important',
        },
      };
    case 'pending':
    case 'idvNotComplete':
      return {
        color: darkMode ? '#6A6A6A !important' : '#8F8F8F !important',
        border: 'none !important',
        background: darkMode ? 'rgba(255, 255, 255, 0.07) !important' : '#F2F2F2 !important',
        lineHeight: 'normal',
        boxShadow: darkMode ? 'none !important' : '0px 1px 2px 0px rgba(0, 0, 0, 0.20) inset !important',
        '&:hover, &:active, &:focus': {
          color: darkMode ? '#6A6A6A !important' : '#8F8F8F !important',
          border: 'none !important',
          background: darkMode ? 'rgba(255, 255, 255, 0.07) !important' : '#F2F2F2 !important',
          boxShadow: darkMode ? 'none !important' : '0px 1px 2px 0px rgba(0, 0, 0, 0.20) inset !important',
        },
      };
    case 'idVerified':
      return {
        border: 'none !important',
        color: darkMode ? '#AFB0B1 !important' : '#595959 !important',
        background: darkMode ? 'rgba(255, 255, 255, 0.07) !important' : '#F6F4F4 !important',
        boxShadow: 'none !important',
        cursor: 'default !important',
        '&:hover, &:active, &:focus': {
          border: 'none !important',
          color: darkMode ? '#AFB0B1 !important' : '#595959 !important',
          background: darkMode ? 'rgba(255, 255, 255, 0.07) !important' : '#F6F4F4 !important',
          boxShadow: 'none !important',
          cursor: 'default !important',
        },
      };
    case 'unsuccessful':
      return {
        border: 'none !important',
        color: darkMode ? '#F08EA4 !important' : '#DD0B24 !important',
        background: darkMode ? '#352528 !important' : '#FCE8ED !important',
        boxShadow: 'none !important',
        '&:hover, &:active, &:focus': {
          border: 'none !important',
          color: darkMode ? '#F08EA4 !important' : '#DD0B24 !important',
          background: darkMode ? '#352528 !important' : '#FCE8ED !important',
          boxShadow: 'none !important',
        },
      };
    default:
      return {};
  }
};

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const BtnVerification = styled(Button)(({ status }) => {
  const getIdVerifiedStyles = status ? getButtonStyles(status) : {};
  const firstDivStyles = { padding: 'inherit' };

  if (status === 'idVerified') {
    firstDivStyles.padding = '0';
  }
  return {
    width: '100%',
    maxWidth: '100%',
    height: '45px',
    borderRadius: '4px !important',
    padding: '0 !important',
    pointerEvents: 'auto',
    '& > div:first-of-type': firstDivStyles,
    ...getIdVerifiedStyles,
  };
});

const BtnVerificationContent = styled('div')<{ status: string }>(({ status }) => ({
  fontWeight: 700,
  display: 'flex',
  alignItems: status === 'idvNotComplete' ? 'flex-start' : 'center',
  justifyContent: 'center',
  fontFamily: FONT_FAMILIES.primary,
  letterSpacing: '-0.3px',
}));

const getBadgeStyled = () => ({
  marginRight: 3,
});

const IDVButtonV2 = ({
  status,
  onClick,
  customStyle,
  buttonContent,
  ...rest
}: {
  status: string;
  onClick?: () => void;
  customStyle?: React.CSSProperties;
  buttonContent: ReactElement;
}) => {
  return (
    <BtnVerification
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      status={status}
      onClick={onClick}
      customStyle={customStyle}
      {...rest}
    >
      <BtnVerificationContent status={status}>
        {!['getIdVerified', 'unsuccessful', 'idVerified'].includes(status) && (
          <WhiteIcon customStyle={{ width: 'inherit', height: 'inherit', ...getBadgeStyled() }} fillColor={'#2B8FD7'} />
        )}

        {status === 'unsuccessful' && (
          <UnsuccessfulIcon
            customStyle={{ width: 'inherit', height: 'inherit', marginRight: 3, fontSize: 16 }}
            fillColor={isDarkMode() ? '#F08EA4' : '#DD0B24'}
          />
        )}

        {status === 'idVerified' && (
          <VerifiedIcon customStyle={{ width: 21, height: 20, marginRight: 3 }} fillColor={'#2B8FD7'} />
        )}

        {status === 'getIdVerified' ? (
          isDarkMode() ? (
            <DefaultIconV2
              customStyle={{ width: 'inherit', height: 'inherit', marginRight: 3 }}
              fillColor={'#212121'}
            />
          ) : (
            <WhiteIcon
              customStyle={{ width: 'inherit', height: 'inherit', ...getBadgeStyled() }}
              fillColor={'#2B8FD7'}
            />
          )
        ) : null}

        <div style={{ fontSize: 14 }}>{buttonContent}</div>
      </BtnVerificationContent>
    </BtnVerification>
  );
};

export default IDVButtonV2;
